/**
 * useFocusRow — scrolls + highlights a row whose `[data-focus-id]` matches
 * the `?focus=` query param.
 *
 * Usage:
 *   useFocusRow([rows]);                // most pages: retry until data lands
 *
 * Behavior:
 *   - No-op when ?focus= is absent.
 *   - Polls DOM up to 15x (every 100ms) so the highlight works even if the
 *     list takes a bit to render after a fetch.
 *   - Scrolls the row into view (centered, smooth) and applies the
 *     `focus-row-highlight` CSS class for ~2s.
 *   - Runs once per `focus` value; re-navigating to the same id does not
 *     re-trigger (by design — avoid distracting flashes on every re-render).
 */
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export function useFocusRow(deps = []) {
  const [sp] = useSearchParams();
  const focusId = sp.get("focus");
  const triggeredFor = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!focusId || triggeredFor.current === focusId) return;
    let tries = 0;
    const interval = setInterval(() => {
      const el = document.querySelector(
        `[data-focus-id="${window.CSS?.escape?.(focusId) || focusId}"]`
      );
      if (el) {
        clearInterval(interval);
        triggeredFor.current = focusId;
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        el.classList.add("focus-row-highlight");
        setTimeout(() => el.classList.remove("focus-row-highlight"), 2200);
      } else if (++tries > 15) {
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [focusId, ...deps]);
}
